/**
 * Commit Rendering Progress Dialog
 *
 * Shows a Premiere/Resolve-style rendering progress dialog while the worker
 * processes the timeline commit in the background.
 */

import { useEffect, useState } from 'react'
import { getJob, type JobResponse } from '@/lib/api/commits'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Progress } from '@/components/ui/progress'
import { Loader2, CheckCircle2, XCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'

interface CommitRenderingDialogProps {
  jobId: string
  sequenceId: string
  onComplete: (commitId: string) => void
  onError: (error: string) => void
}

export function CommitRenderingDialog({
  jobId,
  onComplete,
  onError,
}: CommitRenderingDialogProps) {
  const [job, setJob] = useState<JobResponse | null>(null)
  const [isOpen, setIsOpen] = useState(true)

  // Poll for job status
  useEffect(() => {
    let pollInterval: number

    const pollJob = async () => {
      try {
        const jobData = await getJob(jobId)
        setJob(jobData)

        // Check if job is complete
        if (jobData.status === 'completed') {
          clearInterval(pollInterval)
          setTimeout(() => {
            setIsOpen(false)
            // Extract commit_id from result_data
            const commitId = jobData.result_data?.commit_id
            if (commitId) {
              onComplete(commitId)
            } else {
              onError('Commit created but ID not found in response')
            }
          }, 1000) // Show success for 1 second before closing
        } else if (jobData.status === 'failed') {
          clearInterval(pollInterval)
          setTimeout(() => {
            setIsOpen(false)
            onError(jobData.error_message || 'Timeline processing failed')
          }, 2000) // Show error for 2 seconds before closing
        }
      } catch (error) {
        console.error('Failed to poll job:', error)
        clearInterval(pollInterval)
        setIsOpen(false)
        onError(error instanceof Error ? error.message : 'Failed to check job status')
      }
    }

    // Start polling immediately
    pollJob()
    pollInterval = setInterval(pollJob, 1000) // Poll every second

    return () => {
      if (pollInterval) {
        clearInterval(pollInterval)
      }
    }
  }, [jobId, onComplete, onError])

  // Get user-friendly status message
  const getStatusMessage = () => {
    if (!job) return 'Starting...'

    if (job.status_message) {
      // Map technical messages to user-friendly ones
      const message = job.status_message.toLowerCase()
      if (message.includes('parsing') || message.includes('download')) {
        return 'Rendering timeline...'
      } else if (message.includes('sync') || message.includes('clip')) {
        return 'Rendering clips...'
      } else if (message.includes('finali') || message.includes('activity')) {
        return 'Finalizing...'
      }
      return job.status_message
    }

    return 'Rendering timeline...'
  }

  // Get progress percentage
  const progress = job?.progress_percent || 0
  const isComplete = job?.status === 'completed'
  const isFailed = job?.status === 'failed'

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-md" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isComplete ? (
              <>
                <CheckCircle2 className="h-5 w-5 text-green-500" />
                <span>Timeline Rendered</span>
              </>
            ) : isFailed ? (
              <>
                <XCircle className="h-5 w-5 text-red-500" />
                <span>Rendering Failed</span>
              </>
            ) : (
              <>
                <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
                <span>Rendering Timeline...</span>
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            {isComplete
              ? 'Your timeline has been successfully rendered'
              : isFailed
              ? job?.error_message || 'An error occurred while rendering'
              : getStatusMessage()}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Progress Bar - Premiere/Resolve style */}
          <div className="space-y-2">
            <Progress
              value={progress}
              className="h-3 bg-gray-200"
              indicatorClassName={
                isComplete
                  ? "bg-green-500"
                  : isFailed
                  ? "bg-red-500"
                  : "bg-blue-500 animate-pulse"
              }
            />
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>{Math.round(progress)}%</span>
              {!isComplete && !isFailed && progress < 30 && (
                <span className="text-xs italic">Loading... grab a coffee, this isn't a one-take</span>
              )}
              {!isComplete && !isFailed && progress >= 30 && progress < 45 && (
                <span className="text-xs italic">Reading the timeline... "Can we make it shorter?"</span>
              )}
              {!isComplete && !isFailed && progress >= 45 && progress <= 60 && (
                <span className="text-xs italic">"We'll fix it in post..." Processing clips, hang tight!</span>
              )}
              {!isComplete && !isFailed && progress > 60 && progress < 85 && (
                <span className="text-xs italic">Checking for VFX magic... no green screens were harmed</span>
              )}
              {!isComplete && !isFailed && progress >= 85 && progress < 100 && (
                <span className="text-xs italic">Adding final touches... (no, we can't make it more "epic") ✨</span>
              )}
            </div>
          </div>

          {/* Technical Details (for debugging) */}
          {job && !isComplete && !isFailed && (
            <div className="text-xs text-muted-foreground space-y-1">
              {job.current_step && (
                <div>Step: {job.current_step}</div>
              )}
            </div>
          )}

          {/* Error Details */}
          {isFailed && job?.error_message && (
            <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md">
              {job.error_message}
            </div>
          )}
        </div>

        {/* Only show close button if failed */}
        {isFailed && (
          <div className="flex justify-end">
            <Button
              variant="outline"
              onClick={() => {
                setIsOpen(false)
                onError(job?.error_message || 'Rendering failed')
              }}
            >
              Close
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
